/**
 * EventSummary Component
 * Grid of event count cards for the dashboard
 * Always shows all 6 standard events, fills counts from API data
 */

import React from 'react';
import './EventSummary.css';

const EVENT_ICONS = {
  PageView: (
    <svg viewBox="0 0 24 24">
      <path fill="currentColor" d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
    </svg>
  ),
  ViewContent: (
    <svg viewBox="0 0 24 24">
      <path fill="currentColor" d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
    </svg>
  ),
  AddToCart: (
    <svg viewBox="0 0 24 24">
      <path fill="currentColor" d="M17,18A2,2 0 0,1 19,20A2,2 0 0,1 17,22C15.89,22 15,21.1 15,20C15,18.89 15.89,18 17,18M1,2H4.27L5.21,4H20A1,1 0 0,1 21,5C21,5.17 20.95,5.34 20.88,5.5L17.3,11.97C16.96,12.58 16.3,13 15.55,13H8.1L7.2,14.63L7.17,14.75A0.25,0.25 0 0,0 7.42,15H19V17H7C5.89,17 5,16.1 5,15C5,14.65 5.09,14.32 5.24,14.04L6.6,11.59L3,4H1V2M7,18A2,2 0 0,1 9,20A2,2 0 0,1 7,22C5.89,22 5,21.1 5,20C5,18.89 5.89,18 7,18Z" />
    </svg>
  ),
  InitiateCheckout: (
    <svg viewBox="0 0 24 24">
      <path fill="currentColor" d="M19,3H14.82C14.4,1.84 13.3,1 12,1C10.7,1 9.6,1.84 9.18,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M12,3A1,1 0 0,1 13,4A1,1 0 0,1 12,5A1,1 0 0,1 11,4A1,1 0 0,1 12,3M7,7H17V5H19V19H5V5H7V7M7.5,13.5L9,12L11,14L15.5,9.5L17,11L11,17L7.5,13.5Z" />
    </svg>
  ),
  Purchase: (
    <svg viewBox="0 0 24 24">
      <path fill="currentColor" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
    </svg>
  ),
  Search: (
    <svg viewBox="0 0 24 24">
      <path fill="currentColor" d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
    </svg>
  )
};

// All 6 standard events — always shown
const ALL_EVENTS = [
  { key: 'PageView',         label: 'Page Views',        color: '#4f46e5' },
  { key: 'ViewContent',      label: 'Content Views',     color: '#0d9488' },
  { key: 'AddToCart',        label: 'Add to Cart',       color: '#d97706' },
  { key: 'InitiateCheckout', label: 'Checkouts',         color: '#7c3aed' },
  { key: 'Purchase',         label: 'Purchases',         color: '#059669' },
  { key: 'Search',           label: 'Searches',          color: '#2563eb' },
];

const EventSummary = ({ events, isLoading, hasAccessToken }) => {
  if (isLoading) {
    return (
      <div className="meta-events-grid">
        {ALL_EVENTS.map((e) => (
          <div key={e.key} className="meta-event-card meta-skeleton" style={{ height: '110px' }} />
        ))}
      </div>
    );
  }

  if (!hasAccessToken) {
    return (
      <div className="meta-events-empty">
        <div className="meta-events-empty-icon">
          <svg viewBox="0 0 24 24">
            <path fill="currentColor" d="M19,3H14.82C14.4,1.84 13.3,1 12,1C10.7,1 9.6,1.84 9.18,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M12,3A1,1 0 0,1 13,4A1,1 0 0,1 12,5A1,1 0 0,1 11,4A1,1 0 0,1 12,3Z" />
          </svg>
        </div>
        <p className="meta-events-empty-text">
          Add Meta access token to see event data
        </p>
      </div>
    );
  }

  // Build a lookup map from API data — key: event name → count
  const apiDataMap = {};
  if (events && events.length > 0) {
    events.forEach(e => {
      apiDataMap[e.event] = {
        count: e.count || 0,
        countFormatted: e.countFormatted || String(e.count || 0)
      };
    });
  }

  return (
    <div className="meta-events-grid">
      {ALL_EVENTS.map((eventDef) => {
        const apiData = apiDataMap[eventDef.key];
        const count = apiData?.count || 0;
        const countFormatted = apiData?.countFormatted || '0';
        const color = eventDef.color;

        return (
          <div key={eventDef.key} className="meta-event-card">
            <div
              className="meta-event-icon"
              style={{ backgroundColor: `${color}1a` }}
            >
              <div style={{ color }}>{EVENT_ICONS[eventDef.key]}</div>
            </div>
            <div className="meta-event-count">{countFormatted}</div>
            <div className="meta-event-label">{eventDef.label}</div>
          </div>
        );
      })}
    </div>
  );
};

export default EventSummary;